import { SettlementCategory } from './types';

export type SettlementIssueKind = 'new' | 'reRegistration' | null;
export type SettlementIssueKindSource = 'input' | 'default' | 'inherited' | 'corrected' | 'backfill' | null;

const SOURCE_LABEL: Record<string, string> = {
    default: '기본값',
    inherited: '인계',
    corrected: '정정',
    backfill: '보충',
};

export interface SettlementBasisView {
    /** 수강권에 정산 기준이 기록돼 있는가. 키 자체가 없으면 배포 전 수강권(보충 전). */
    recorded: boolean;
    categoryText: string;
    kindText: string;
    /** 발급 유형의 출처가 사람이 고른 값(input)이 아니면 작은 회색 표시. */
    sourceText: string | null;
    /** isNewSales(신규/재등록 표시)와 정산 기준이 다를 때 안내. */
    mismatchNote: string | null;
    categoryKey: string | null;
    issueKind: SettlementIssueKind;
}

const has = (obj: unknown, key: string) => !!obj && Object.prototype.hasOwnProperty.call(obj, key);

/** 기본 정산의 신규/재등록 판단 안내 — 서버 base 는 발급 유형이 아니라 수강권의 isNewSales 로 고른다. */
export const BASE_SETTLEMENT_KIND_NOTE = '신규/재등록은 수강권의 신규·재등록 표시를 따라요.';

/**
 * 수강권의 「정산 기준」(발급 시 기록) 표시. 값은 서버가 기록한 그대로 보이고 웹에서 추정하지 않는다.
 * - 키 없음 = 배포 전 수강권 → 「기록 없음」
 * - null = 근거 없음 → 분류는 「미분류」, 유형은 「정해지지 않음」
 * - settlementType 'base': 서버 base 는 분류만 쓰고 신규/재등록은 isNewSales 로 정한다(발급 유형을 보지 않는다).
 *   그래서 유형 문구·「발급 때 기준」 불일치 안내를 내지 않는다(kindText 빈 값, mismatchNote null).
 */
export const describeSettlementBasis = (
    membership: unknown,
    isNewSales: boolean | null | undefined,
    categoryMap: Map<string, SettlementCategory>,
    settlementType: string = 'tier',
): SettlementBasisView => {
    const m = (membership ?? {}) as Record<string, unknown>;
    const recorded = has(m, 'settlementCategoryKey') || has(m, 'settlementIssueKind');
    const categoryKey = typeof m.settlementCategoryKey === 'string' ? m.settlementCategoryKey : null;
    const issueKind: SettlementIssueKind =
        m.settlementIssueKind === 'new' || m.settlementIssueKind === 'reRegistration' ? m.settlementIssueKind : null;
    const source = typeof m.settlementIssueKindSource === 'string' ? m.settlementIssueKindSource : null;

    if (!recorded) {
        return {
            recorded,
            categoryText: '기록 없음',
            kindText: '',
            sourceText: null,
            mismatchNote: null,
            categoryKey: null,
            issueKind: null,
        };
    }

    const category = categoryKey ? categoryMap.get(categoryKey) : undefined;
    const categoryText = !categoryKey ? '미분류' : category ? category.name : '알 수 없는 분류';
    if (settlementType === 'base') {
        return { recorded, categoryText, kindText: '', sourceText: null, mismatchNote: null, categoryKey, issueKind };
    }
    const kindText = issueKind === 'new' ? '신규' : issueKind === 'reRegistration' ? '재등록' : '유형 정해지지 않음';

    let mismatchNote: string | null = null;
    if (issueKind && typeof isNewSales === 'boolean') {
        const displayKind = isNewSales ? 'new' : 'reRegistration';
        if (displayKind !== issueKind) {
            mismatchNote = `위 신규/재등록 표시는 지금 기준, 정산은 발급 때 기준(${kindText})이에요.`;
        }
    }

    return {
        recorded,
        categoryText,
        kindText,
        sourceText: source && SOURCE_LABEL[source] ? SOURCE_LABEL[source] : null,
        mismatchNote,
        categoryKey,
        issueKind,
    };
};

/**
 * 수강권 정보의 「정산 기준」을 보일지.
 *
 * 새 기능을 안 쓰는 센터에게는 바꾸기 전 화면과 같아야 한다. 그래서
 * 정산 방식이 알려진 센터(tier·sessionTier·base — 예외 수업료가 세 방식 모두에서 계산된다)이고
 * 서버에 기능이 있을 때(분류 GET 200) 중에서도
 * - 센터에 정산 분류가 1개 이상 있거나(보관 포함),
 * - 이 수강권이 정정된 적이 있을 때(settlementIssueKindSource = 'corrected')만 보인다.
 * 「예외 규칙(재등록 포함)을 쓰는 센터」도 보이는 게 맞지만 이 화면에서 읽을 수 있는 서버 신호가 없다
 * (요율 조회는 강사·월 단위) — 보고서에 필요한 신호를 적었다.
 */
export const shouldShowSettlementBasis = ({
    settlementType,
    available,
    categoryCount,
    source,
}: {
    settlementType: unknown;
    available: boolean;
    categoryCount: number;
    source: unknown;
}) =>
    (settlementType === 'tier' || settlementType === 'sessionTier' || settlementType === 'base') &&
    available &&
    (categoryCount > 0 || source === 'corrected');

/**
 * 발급 유형(settlementIssueKind) 키가 없는 옛 수강권인가. 이런 수강권은 서버가 지금의 신규/재등록 표시(isNewSales)로
 * 재등록 수업료를 정한다(서버 10/18 F6, isIssueKindFallbackCandidate). 그래서 표시를 바꾸면 지난 달 급여도 바뀔 수 있다.
 */
export const isIssueKindFallbackMembership = (membership: unknown): boolean =>
    !!membership && typeof membership === 'object' && !has(membership, 'settlementIssueKind');

/** 급여 테이블 응답에 재등록 예외 규칙을 가진 강사가 있는가(그 달 기준). 응답을 못 읽으면 null. */
export const hasReRegistrationExceptionRule = (tables: unknown): boolean | null => {
    const list = (tables as { coachCommissionTables?: unknown } | null | undefined)?.coachCommissionTables;
    if (!Array.isArray(list)) return null;
    return list.some((table) =>
        ((table as { exceptionRates?: unknown })?.exceptionRates as Array<{ match?: { type?: string } }> | undefined)?.some?.(
            (rule) => rule?.match?.type === 'reRegistration',
        ),
    );
};

/**
 * 수강권 수정 창에서 옛 수강권의 신규/재등록 표시를 바꿀 때 「지난 달 급여도 바뀔 수 있어요」 확인이 필요한가(규칙 조회 전 1차 조건).
 * 재등록 예외 규칙의 대체 판정(isNewSales)을 쓰는 상세·세션별 정산만. 기본 정산은 재등록 예외를 쓰지 않는다
 * (기본 정산이 isNewSales 로 신규/재등록 요율을 고르는 것은 원래 동작이고 이 안내 대상이 아니다).
 */
export const shouldCheckIssueKindFallbackNotice = (
    settlementType: unknown,
    isDisplayChanged: boolean,
    membership: unknown,
): boolean =>
    (settlementType === 'tier' || settlementType === 'sessionTier') &&
    isDisplayChanged &&
    isIssueKindFallbackMembership(membership);

export const ISSUE_KIND_FALLBACK_CHANGE_NOTICE =
    '발급 기록이 없는 옛 수강권은 이 표시로 재등록 수업료가 정해져요. 바꾸면 지난 달 급여도 바뀔 수 있어요.';

